import merge from 'lodash/merge'

import { useSessionProvider } from '@shared/composables/use-session-provider'
import en from '@shared/i18n/locales/en'
import getPersonType from '@shared/utils/helpers/person-type'

const generateConfig = (personType: string) => {
  return merge(en, {
    vc: {
      http: {
        errors: {
          widgetsList: 'Failed to fetch a list of widgets',
          quickForm: 'Failed to fetch a quick form data',
          quickSignTemplate: 'Failed to fetch a Quick Sign Template data',
          cannotDeleteInvintation:
            'Cannot delete invitation. You are not the conversation initiator',
          updateConversation: 'Failed to update conversation',
          assiigneeChanged: 'Current Assignee has Changed.',
        },
      },
      sender: {
        email: 'Invitation Email',
        sms: 'Invitation SMS',
        from: 'From: {from}',
        to: 'To: {to}',
        subject: 'Subject: {subject}',
      },
      tac: {
        banner: {
          declined: 'Terms and Conditions are declined',
          presented: 'Terms and Conditions are presented',
          accepted: 'Terms and Conditions are accepted',
          remindToAccept: 'Remind to accept',
        },
        notAccepted: 'Terms and Conditions are not accepted',
        messageBody:
          'You have not agreed to the Terms and Conditions. We can not proceed with the conversation until you agree. Please click the link and accept the Terms and Conditions.',
        messageSubject: 'Terms and Conditions',
        sendReminder: 'Send reminder',
        reminderDialogtitle: 'Send Reminder to ',
        messages: {
          success: {
            remindTac: 'Remind Terms and Condition has been successfully sent!',
          },
          error: {
            remindTac:
              'Failed to send remind Terms and Condition. Please try again later.',
          },
        },
      },
      labels: {
        emr: 'Emr',
        file: 'File',
        private: 'Private',
        translate: 'Translate',
        createPrivatePatient: `Create Private ${personType}`,
        createNewPatient: `Create New ${personType}`,
        patientFullName: `${personType} First name and Last name`,
        search: 'Search',
        createNew: 'Create new',
        remoteCameraOff: '{name} has turned off their camera',
        remoteMicrophoneOff: '{name} has turned off their microphone',
        chatWidgetToSend: 'Chat widget to send',
        widgetNumber: 'Widget Number',
        searchForm: 'Find form',
        forms: 'Forms',
        background: {
          blur: 'Blur',
          disable: 'None',
          custom: 'Custom',
          default: 'Default',
          replace: 'Replace Custom',
          add: 'Add Custom',
        },
        videocallTestFailed:
          'The patient has failed the self test. The visit may not be successful if you continue',
        pleaseSelectWidget: 'Please select a widget to start a conversation',
      },
      messages: {
        backgroungImageError: 'Failed to upload background image',
        assigneeUpdatedError:
          'Failed to change an assignee of the conversation. Please try again later',
        failedToLoadConversations: 'Failed to load conversations',
        assigneeUpdated: 'A conversation assignee has been updated!',
        ongoingCall: 'Ongoing call {duration}',
        doesntExist: 'does not exist',
        cannotReassign: 'Conversation cannot be re-assigned.',
        reassignIssueDescription:
          "You can't re-assign the conversation because there is a problem with the related widget. It was probably deleted. Please create a new conversation within an existing widget and try again.",
        newAssignmentsInfo: 'When a conversation is assigned to you',
      },
      start: 'Start',
      unreadMessages: 'Unread Messages',
      newAssignments: 'New Assignments',
      pickQuickMessage: 'Pick a Quick Message',
      pickQuickForm: 'Pick a Quick Form',
      quickSignSubtitle: 'Easy way to get digital signatures done',
      uploadPdf: 'Upload PDF',
      fillLablesTooltip: 'Fill-in place holder labels',
      notificationsTooltip:
        'New messages and assignments notifications will be shown here',
      muteWaitingRoomTitle: 'Notifications for Waiting Room Conversations.',
      muteWaitingRoomText:
        'Turn on or off notifications when the waiting room bot finishes. This notification helps Agent to take over the conversation once the Bot finishes interacting',
      quickSign: {
        updatedSuccess: 'Quick Sign Template updated successfully',
        editTemplate: 'Edit template',
        saveAs: 'Save as',
        name: 'Quick Sign',
        savedSuccess: 'Quick Sign Template saved successfully',
        savedError: 'Failed to save Quick Sign Template',
        editError: 'Failed to edit Quick Sign Template',
        viewTemplates: 'View templates',
        saveAsTemplate: 'Save as Template',
        templateName: 'Template Name',
        templateNamePaceholder: 'Enter Template Name',
        templateModalTitle: 'Pick a Quick Sign Template',
        filterPrivate: 'Filter only private',
        preview: 'Preview',
        findTemplate: 'Find Quick Sign template',
        openTempleteEdit: 'View Template',
        selectTemplate: 'Select a Template to preview',
      },
      attachments: {
        title: 'Attachments',
        forms: 'Quick Forms',
      },
      quickFormsEmpty: {
        admin: 'Currently there are no created forms for this group.',
        agent:
          'Currently there are no created forms for this group. Please reach out to your group admin.',
      },
      emrSearch: {
        lastName: 'Last Name',
        firstName: 'First Name',
        mobileNumber: 'Mobile Number',
        email: 'Email',
        mrn: 'MRN',
        selectGroup: `At least one ${personType} group should be selected`,
      },
      createModal: {
        source: 'Source',
        allPatients: `All ${personType}s`,
        patientGroups: `${personType} groups`,
        placeholderGroupType: `Select ${personType} group`,
        placeholderGroup: `Select ${personType} groups`,
        tooltip:
          'Once the integration with your EMR or other data sources is complete, you will be able to search for patients directly from those systems, ensuring seamless access to the information you need.',
      },
      plusChat: {
        myPatients: `My ${personType}s`,
        setPatient: `Set ${personType}`,
        errors: {
          noPatientsFound: `No ${personType}s found`,
          noPatientInsuranceCarrier: `Don't see ${personType}'s insurance carrier?`,
          noEmrPatientChosen: `No EMR capable ${personType} chosen.`,
          noPatientSet: `Please set a ${personType}.`,
          selectingPatient: `Error while selecting ${personType}`,
        },
        helpText: {
          cantFindPatient: `Can't find your ${personType}?`,
        },
        labels: {
          patientInsuranceSelect: `Pick ${personType}'s insurance`,
          searchPatientIn: `Search ${personType} in`,
        },
        messages: {
          destinationPatientChanged: `Successfully Changed Destination ${personType}`,
        },
        titles: {
          invitePatient: `Invite ${personType}`,
        },
        tooltips: {
          privatePatients: `Search only your ${personType}s`,
        },
      },
    },
  })
}

const dynamicConfig = generateConfig(
  getPersonType().getTypeStringByType(useSessionProvider().patientType.value)
)

export default dynamicConfig
